export type RecordingRole = 'owner' | 'participant';
export type RecordingStatus = 'starting' | 'active' | 'complete' | 'failed';
export type ShareAudience = 'organization' | 'public';

export interface Recording {
    id: string;
    roomName: string;
    startedAt: string;
    endedAt: string | null;
    durationMs: number | null;
    sizeBytes: number | null;
    status: RecordingStatus;
    role: RecordingRole | null;
    transcribe: boolean;
}

export interface RecordingList {
    recordings: Recording[];
    publicLinksEnabled: boolean;
}

export interface RecordingShare {
    id: string;
    audience: ShareAudience;
    createdAt: string;
    expiresAt: string | null;
}

export interface SharedRecording {
    recording: Recording;
    expiresAt: string | null;
}

export class ApiError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.status = status;
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(path, init);
    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string };
        throw new ApiError(response.status, body.error ?? `${String(response.status)} ${response.statusText}`);
    }
    return (response.status === 204 ? undefined : await response.json()) as T;
}

const recordingPath = (id: string) => `/api/recordings/${encodeURIComponent(id)}`;

export const recordingsApi = {
    list: () => request<RecordingList>('/api/recordings'),
    url: (id: string, download = false) =>
        request<{ url: string }>(`${recordingPath(id)}/url${download ? '?download' : ''}`).then((body) => body.url),
    remove: (id: string) => request<undefined>(recordingPath(id), { method: 'DELETE' }),
    shares: (id: string) => request<{ shares: RecordingShare[] }>(`${recordingPath(id)}/shares`).then((body) => body.shares),
    createShare: (id: string, body: { audience: ShareAudience; expiresInDays: number | null }) =>
        request<{ share: RecordingShare; url: string }>(`${recordingPath(id)}/shares`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }),
    revokeShare: (id: string, shareId: string) =>
        request<undefined>(`${recordingPath(id)}/shares/${encodeURIComponent(shareId)}`, { method: 'DELETE' }),
    shared: (token: string) => request<SharedRecording>(`/api/shared/${encodeURIComponent(token)}`),
    sharedUrl: (token: string, download = false) =>
        request<{ url: string }>(`/api/shared/${encodeURIComponent(token)}/url${download ? '?download' : ''}`).then(
            (body) => body.url
        ),
};
